import React, { useMemo, useState } from "react";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
} from "./StrategyCanvasHelpers";
import useStrategyCanvasStore, {
  useStrategyCanvasStoreSelector,
} from "./_redux/StrategyCanvasStore";

const EmptyDocsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <rect
      x="6"
      y="4"
      width="16"
      height="21"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M10.5 4V3.5A1.5 1.5 0 0 1 12 2h4a1.5 1.5 0 0 1 1.5 1.5V4"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M10 12.5h8M10 16.5h8M10 20.5h5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const fileExt = (file) =>
  (file?.name?.includes(".")
    ? file.name.split(".").pop()
    : "FILE"
  ).toUpperCase();

const toFileRow = (item) => {
  const file = item?.file || item;
  return { ext: fileExt(file), name: file?.name || "", fileId: file?.file_id };
};

const filesFromBlock = (block) => {
  if (Array.isArray(block?.items)) return block.items.map(toFileRow);
  if (block?.item) return [toFileRow(block.item)];
  return [];
};

const fileCountLabel = (count) =>
  count ? `${count} ${count === 1 ? "File" : "Files"}` : "-";

const buildLinksSection = (block = {}) => ({
  status: block.filled ? "done" : "none",
  countLabel: block.count ? `${block.count} URLS` : "-",
  type: block.count ? "links" : undefined,
  items: (block.items || []).map((item) => item.url),
});

const buildTextsSection = (block = {}) => ({
  status: block.filled ? "done" : "none",
  countLabel: block.count ? `${block.count} TEXTS` : "-",
  type: block.count ? "texts" : undefined,
  items: (block.items || []).map((item) => item.text),
});

const buildFilesSection = (block = {}) => {
  const files = filesFromBlock(block);
  return {
    status: block.filled ? "done" : "none",
    countLabel: fileCountLabel(files.length),
    type: files.length ? "files-flat" : undefined,
    files,
  };
};

// Maps the `blocks` object from GET /coach/strategy-canvas/:uniqueId/documents
// onto a flat, fixed-order section list. mpvv is intentionally excluded -
// we never show it here. Sections with no data are dropped entirely (no
// heading shown) rather than rendered as an empty/collapsed row; if every
// section is empty, the whole tab falls back to the "no docs" state.
const buildDocumentSections = (blocks = {}) =>
  [
    {
      id: "website",
      title: "Website",
      ...buildLinksSection(blocks.website),
    },
    {
      id: "about",
      title: "Tell us more about your business",
      ...buildTextsSection(blocks.about),
    },
    {
      id: "business-documents",
      title: "Business documents",
      ...buildFilesSection(blocks.business_documents),
    },
    {
      id: "financials",
      title: "Financial documents",
      ...buildFilesSection(blocks.financials),
    },
    {
      id: "product-revenue",
      title: "Product/service list with revenue mix",
      ...buildFilesSection(blocks.product_revenue),
    },
  ].filter((section) => section.status !== "none");

const selectDownloadingFileIds = (state) => state.downloadingFileIds;

// Section 4.2 field notes - GET /shared/download/coach/:file_id.
const FileRow = ({ fileId, ext, name }) => {
  const downloadingFileIds = useStrategyCanvasStoreSelector(
    selectDownloadingFileIds,
  );
  const isDownloading = fileId ? !!downloadingFileIds[fileId] : false;

  const handleDownload = () => {
    useStrategyCanvasStore.getState().downloadSharedFile(fileId, name);
  };

  return (
    <div className="scb-doc-file-row">
      <span className="scb-doc-file-badge">{ext}</span>
      <span className="scb-doc-file-name">{name}</span>
      <button
        className="scb-doc-icon-btn"
        aria-label={`Download ${name}`}
        title="Download file"
        onClick={handleDownload}
        disabled={!fileId || isDownloading}
      >
        <DownloadIcon />
      </button>
    </div>
  );
};

const copyToClipboard = (text) => {
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text);
};

const SectionContent = ({ section }) => {
  switch (section.type) {
    case "links":
      return (
        <div className="scb-doc-list">
          {section.items.map((url, index) => (
            <div className="scb-doc-link-row" key={index}>
              <a href={url} target="_blank" rel="noreferrer">
                {url}
              </a>
              <ExternalLinkIcon size={12} />
            </div>
          ))}
        </div>
      );
    case "texts":
      return (
        <div className="scb-doc-texts">
          {section.items.map((text, index) => (
            <div className="scb-doc-text-box" key={index}>
              <p>{text}</p>
              <button
                className="scb-doc-icon-btn"
                aria-label="Copy text"
                onClick={() => copyToClipboard(text)}
              >
                <CopyIcon />
              </button>
            </div>
          ))}
        </div>
      );
    case "files-flat":
      return (
        <div className="scb-doc-list">
          {section.files.map((file, index) => (
            <FileRow
              fileId={file.fileId}
              ext={file.ext}
              name={file.name}
              key={index}
            />
          ))}
        </div>
      );
    default:
      return null;
  }
};

// Every section reaching here already has data (empty ones are filtered out
// before render), so the header always shows the "done" check.
const DocumentSection = ({ section }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="scb-doc-section">
      <button
        className="scb-doc-section-header"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="scb-doc-status">
          <CheckCircleIcon />
        </span>
        <span className="scb-doc-section-title">{section.title}</span>
        <span className="scb-doc-section-count">{section.countLabel}</span>
        <span className={`scb-doc-chevron ${isOpen ? "open" : ""}`}>
          <ChevronDownIcon />
        </span>
      </button>
      {isOpen ? (
        <div className="scb-doc-section-body">
          <SectionContent section={section} />
        </div>
      ) : null}
    </div>
  );
};

const StrategyCanvasDocuments = ({ blocks }) => {
  const sections = useMemo(() => buildDocumentSections(blocks), [blocks]);

  if (sections.length === 0) {
    return (
      <div className="scb-doc-empty">
        <EmptyDocsIcon />
        <p>No documents uploaded</p>
      </div>
    );
  }

  return (
    <div className="scb-doc-sections">
      {sections.map((section) => (
        <DocumentSection section={section} key={section.id} />
      ))}
    </div>
  );
};

export default StrategyCanvasDocuments;
